import React, { useEffect, useMemo } from 'react';
import CustomShaderMaterial from '../CustomShaderMaterial';
import { Color, DoubleSide, NormalBlending } from 'three';
import { useFrame } from '@react-three/fiber';

type Props = {
    color?: number | Color,
    alpha?: number,
    outline?: number,
    numDots?: number,
    segmentRatio?: number
    segmentSpeed?: number
};

export default function DottedDiskMaterial({
  color,
  outline,
  numDots,
  segmentRatio,
  alpha,
  segmentSpeed
}: Props): JSX.Element
{
  const uniforms = useMemo(() =>
  {
    return {
      color: { value: new Color() },
      alpha: { value: 1.0 },
      outline: { value: 0.1 },
      segmentScale: { value: 1.0 },
      segmentRatio: { value: 0.5 },
      segmentOffset: { value: 0.5 }
    };
  }, []);

  useEffect(() =>
  {
    if (color instanceof Color)
      uniforms.color.value.copy(color);
    else
      uniforms.color.value.set(color || 0x000000);
  }, [ color, uniforms ]);

  useEffect(() =>
  {
    uniforms.outline.value = outline || 0.0075;
  }, [ outline, uniforms ]);

  useEffect(() =>
  {
    uniforms.alpha.value = alpha === undefined ? 1 : alpha;
  }, [ alpha, uniforms ]);

  useEffect(() =>
  {
    uniforms.segmentScale.value = (numDots || 80) / (2.0 * Math.PI);
  }, [ numDots, uniforms ]);

  useEffect(() =>
  {
    uniforms.segmentRatio.value = segmentRatio || 0.5;
  }, [ segmentRatio, uniforms ]);

  useFrame((state, delta) =>
  {
    if (segmentSpeed !== undefined)
      uniforms.segmentOffset.value += delta * segmentSpeed;
  });

  return <CustomShaderMaterial
    fragmentID="dottedDiskFragmentShader"
    vertexID="dottedDiskVertexShader"
    uniforms={uniforms}
    blending={NormalBlending}
    side={DoubleSide}
    depthWrite={false}
    transparent
  />;
}